/**
 * Project hub helpers — ADR-0033 Project Scope Phase 1 (T8).
 *
 * Pure functions the /ask-argus/project/$projectId page uses to turn the
 * Library / Connections API shapes into the picker's inputs, and to render
 * bound sources. No React, no I/O — unit-tested directly.
 */

import type { RemoteFolderNode } from '@/types/library';
import { normalizeSelectedRepos } from '@argusai/shared/schemas/project-scope';
import type { ProjectSource } from '@argusai/shared/schemas/project-scope';

/** A Library folder as the picker needs it (lib/use-library-folders.ts — the one folder list). */
export interface LibraryFolderRow {
  id: string;
  name: string;
  parentId: string | null;
}

/**
 * Nest flat Library folders by parentId into the RemoteFolderNode tree the
 * existing FolderPicker renders. Siblings are ordered by name; orphans
 * (parentId pointing at a folder not in the list) surface at the root so
 * they stay bindable. `path` is the slash-joined ancestry (display only).
 */
export function buildFolderTree(folders: LibraryFolderRow[]): RemoteFolderNode[] {
  const byId = new Map<string, LibraryFolderRow>();
  for (const f of folders) byId.set(f.id, f);
  const children = new Map<string | null, LibraryFolderRow[]>();
  for (const f of folders) {
    const parent = f.parentId && byId.has(f.parentId) ? f.parentId : null;
    const list = children.get(parent) ?? [];
    list.push(f);
    children.set(parent, list);
  }
  const build = (parent: string | null, prefix: string, seen: Set<string>): RemoteFolderNode[] =>
    (children.get(parent) ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((f) => !seen.has(f.id))
      .map((f) => {
        const path = prefix ? `${prefix}/${f.name}` : f.name;
        const next = new Set(seen);
        next.add(f.id);
        return {
          id: f.id,
          name: f.name,
          path,
          fileCount: 0,
          sizeBytes: 0,
          children: build(f.id, path, next),
        };
      });
  return build(null, '', new Set());
}

/** Minimal connector shape the picker needs (from GET /library/connectors + /:id). */
export interface ConnectorWithRepos {
  id: string;
  displayName: string;
  connectorKind: string;
  config?: { selectedRepos?: unknown } | null;
}

export interface ConnectorRepoOption {
  /** `connectorId:owner/repo` — the project_sources.source_ref for a connector_repo. */
  ref: string;
  connectorId: string;
  connectorName: string;
  ownerRepo: string;
}

/**
 * Flatten GitHub connectors into bindable `connectorId:owner/repo` refs.
 * Connectors without a selectedRepos array (or with an empty one) are
 * skipped — there is nothing to bind.
 *
 * PSP1-02 — `selectedRepos` holds `{ owner, name }` OBJECTS for every real
 * GitHub connector (the adapter's ConfigSchema); the shared normalizer
 * accepts those and the legacy plain-string form. Matching strings only
 * meant the picker listed no repos at all.
 */
export function connectorReposToRefs(connectors: ConnectorWithRepos[]): ConnectorRepoOption[] {
  const out: ConnectorRepoOption[] = [];
  for (const c of connectors) {
    for (const r of normalizeSelectedRepos(c.config?.selectedRepos)) {
      out.push({ ref: `${c.id}:${r}`, connectorId: c.id, connectorName: c.displayName, ownerRepo: r });
    }
  }
  return out;
}

/** What a bound document the viewer may not see (or that is gone) reads as — the server withholds its title. */
export const RESTRICTED_DOCUMENT_LABEL = 'Restricted document';

/**
 * What a bound folder above the viewer's clearance (or one that is gone) reads
 * as — the server withholds its name (founder 2026-10-22: folder names above a
 * person's clearance are hidden).
 */
export const RESTRICTED_FOLDER_LABEL = 'Restricted folder';

/** Human label for a bound source row (kind-aware; falls back to the ref). */
export function sourceLabel(
  source: Pick<ProjectSource, 'sourceKind' | 'sourceRef' | 'label'> & { restricted?: boolean },
): string {
  // SEC-LB4 Finding A — a restricted document comes back with no title, and a
  // restricted folder with no name; each is named neutrally, never by its id.
  if (source.restricted === true) {
    return source.sourceKind === 'folder' ? RESTRICTED_FOLDER_LABEL : RESTRICTED_DOCUMENT_LABEL;
  }
  if (source.label && source.label.trim().length > 0) return source.label;
  if (source.sourceKind === 'connector_repo') {
    const idx = source.sourceRef.indexOf(':');
    return idx >= 0 ? source.sourceRef.slice(idx + 1) : source.sourceRef;
  }
  return source.sourceRef;
}

/** Kind badge text for a bound source row. */
export function sourceKindLabel(kind: ProjectSource['sourceKind']): string {
  switch (kind) {
    case 'folder':
      return 'Folder';
    case 'connector_repo':
      return 'GitHub repo';
    case 'document':
      return 'Document';
    default:
      return String(kind);
  }
}

// REV-6 — `sumDocumentCounts` used to live here. Nothing called it: it
// returned a NON-deduplicated total that contradicts the header's
// de-duplicated `totalDocuments` (a document bound via two sources counted
// twice), so the next caller who reached for it would have double-counted.
// Removed rather than left as a trap; the header reads totalDocuments.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * True only for a real conversation UUID. The project page used to send
 * client-local placeholders (`new-…`, `project-…-…`) as conversationId, which
 * the route's z.string().uuid() rejects; a non-UUID means "create on first
 * send" (the tenant-wide page's documented pattern).
 */
export function isUuid(s: string | null | undefined): s is string {
  return typeof s === 'string' && UUID_RE.test(s);
}
